import React, { useState, useEffect } from "react";
import { Dialog, DialogContent } from "./ui/dialog";
import { Coins, Gift, Ticket, Loader2, CheckCircle2, Trophy, ArrowLeft, Shirt, PoundSterling } from "lucide-react";
import { api } from "../lib/api";

const prizeIcon = (s) => {
  if (s.prize === "tickets" || s.prize === "hospitality") return Ticket;
  if (s.prize === "retro_top") return Shirt;
  if (typeof s.prize === "string" && s.prize.startsWith("cash")) return PoundSterling;
  if (s.coins > 0) return Coins;
  return Gift;
};

const fmtDate = (at) => {
  if (!at) return "";
  try {
    return new Date(at * 1000).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return "";
  }
};

const ClaimForm = ({ spin, onBack, onClaimed }) => {
  const [form, setForm] = useState({ name: "", email: "", note: "" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    if (!form.name.trim() || !form.email.trim()) {
      setError("Name and email are required");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const res = await api.claimSpin(spin.id, form);
      onClaimed(res.spin);
    } catch (e) {
      setSubmitting(false);
      setError(e?.response?.data?.detail || "Couldn't submit — try again");
    }
  };

  const isTickets = spin.prize === "tickets" || spin.prize === "hospitality";
  const isVoucher = typeof spin.prize === "string" && spin.prize.startsWith("cash");

  return (
    <div data-testid="prize-claim-form">
      <button onClick={onBack} className="flex items-center gap-1.5 text-muted-38 text-xs hover:text-white transition-colors mb-3">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to prizes
      </button>
      <div className="font-display font-bold text-lg">Claim your {spin.label}</div>
      <p className="text-muted-38 text-xs mt-1 mb-4">
        {isTickets
          ? "Tell us where to send your hospitality details — our team will be in touch to arrange a matchday."
          : isVoucher
          ? "Drop your email and we'll be in touch shortly to send your prize."
          : "Drop your details and our team will be in touch to sort your prize out."}
      </p>
      <div className="space-y-3">
        <input
          data-testid="claim-name"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          placeholder="Full name"
          maxLength={80}
          className="w-full rounded-xl bg-white/5 border border-white/10 px-4 py-3 text-white placeholder-gray-500 outline-none focus:border-[#10B981]/60"
        />
        <input
          data-testid="claim-email"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          placeholder="Email address"
          type="email"
          className="w-full rounded-xl bg-white/5 border border-white/10 px-4 py-3 text-white placeholder-gray-500 outline-none focus:border-[#10B981]/60"
        />
        <textarea
          data-testid="claim-note"
          value={form.note}
          onChange={(e) => setForm({ ...form, note: e.target.value })}
          placeholder={isTickets ? "Preferred club / match (optional)" : isVoucher ? "Anything else (optional)" : "Delivery address / notes (optional)"}
          maxLength={300}
          rows={2}
          className="w-full rounded-xl bg-white/5 border border-white/10 px-4 py-3 text-white placeholder-gray-500 outline-none focus:border-[#10B981]/60 resize-none"
        />
      </div>
      {error && <p className="text-red-400 text-xs mt-2">{error}</p>}
      <button
        data-testid="claim-submit"
        onClick={submit}
        disabled={submitting}
        className="btn-green w-full rounded-xl py-3 font-semibold mt-4 flex items-center justify-center gap-2 disabled:opacity-60"
      >
        {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting…</> : "Submit claim"}
      </button>
    </div>
  );
};

const PrizesModal = ({ open, onClose }) => {
  const [spins, setSpins] = useState(null);
  const [claiming, setClaiming] = useState(null);

  useEffect(() => {
    if (!open) return;
    setClaiming(null);
    setSpins(null);
    api.pepSpins().then(setSpins).catch(() => setSpins([]));
  }, [open]);

  const onClaimed = (updated) => {
    setSpins((list) => (list || []).map((s) => (s.id === updated.id ? updated : s)));
    setClaiming(null);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="prizes-modal" className="theme-light w-[calc(100%-2rem)] bg-[#1E293B] border border-white/10 text-white max-w-sm p-5">
        {claiming ? (
          <ClaimForm spin={claiming} onBack={() => setClaiming(null)} onClaimed={onClaimed} />
        ) : (
          <>
            <div className="flex items-center gap-2 mb-1">
              <Trophy className="w-5 h-5 text-[#10B981]" />
              <div className="font-display font-bold text-lg">Your prizes</div>
            </div>
            <p className="text-muted-38 text-xs mb-4">Every prize you've spun from beating Pep.</p>

            {spins === null ? (
              <div className="flex justify-center py-10 text-slate-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
            ) : spins.length === 0 ? (
              <div className="text-center py-10" data-testid="prizes-empty">
                <Gift className="w-8 h-8 text-muted-38 mx-auto mb-2" />
                <p className="text-slate-300 text-sm">No prizes yet.</p>
                <p className="text-muted-38 text-xs mt-1">Beat Pep and spin the wheel to start your collection.</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-[340px] overflow-y-auto pr-1">
                {spins.map((s) => {
                  const Icon = prizeIcon(s);
                  return (
                    <div
                      key={s.id}
                      data-testid="prize-row"
                      className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2.5"
                    >
                      <span className="w-9 h-9 rounded-lg bg-[#10B981]/10 border border-[#10B981]/25 flex items-center justify-center shrink-0">
                        <Icon className="w-5 h-5 text-[#10B981]" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold text-white truncate">{s.coins > 0 ? `£${s.coins.toLocaleString()} virtual` : s.label}</div>
                        <div className="text-muted-38 text-[11px]">{fmtDate(s.at)}</div>
                      </div>
                      {s.coins > 0 ? (
                        <span className="text-[#10B981] text-xs font-bold shrink-0">+£{s.coins}</span>
                      ) : s.claimed ? (
                        <span className="flex items-center gap-1 text-emerald-400 text-[11px] font-semibold shrink-0">
                          <CheckCircle2 className="w-3.5 h-3.5" /> Claimed
                        </span>
                      ) : (
                        <button
                          data-testid="prize-claim-btn"
                          onClick={() => setClaiming(s)}
                          className="btn-green rounded-lg px-3 py-1.5 text-xs font-semibold shrink-0"
                        >
                          Claim
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            <button onClick={onClose} className="btn-ghost w-full rounded-xl py-2.5 text-sm font-medium text-slate-300 mt-4">
              Close
            </button>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PrizesModal;
